import { useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { sendBudgetAlertNotification } from '@/services/notifications';
import type { TripAlert } from '@/utils/tripAlerts';
import type { Trip } from '@/services/trips';
import { formatCurrency } from '@/utils/currency';
import { sendGatedNotification, DEFAULT_COOLDOWNS } from '@/services/notificationGate';
import { buildDedupeKey } from '@/services/notificationDedupe';

type StoredBudgetNotification = {
  id: string;
  priority: TripAlert['priority'];
  notifiedAt: number;
};

const STORAGE_KEY = '@tripflow_notified_budget_alerts_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Module-level cache to avoid duplicates during React dev StrictMode
 * (effects can run twice due to mount/unmount/mount).
 * This also helps prevent duplicate sends if the hook is mounted in more than one place.
 */
const runtimeNotified: Map<string, StoredBudgetNotification> = new Map();
let runtimeHydrated = false;
let runtimeHydratePromise: Promise<void> | null = null;

const loadStored = async (): Promise<StoredBudgetNotification[]> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x.id === 'string' && typeof x.notifiedAt === 'number')
      .map((x) => ({
        id: x.id,
        priority: (x.priority as TripAlert['priority']) || 'low',
        notifiedAt: x.notifiedAt,
      }));
  } catch {
    return [];
  }
};

const persistStored = async (entries: StoredBudgetNotification[]) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // best-effort
  }
};

const ensureRuntimeHydrated = async (): Promise<void> => {
  if (runtimeHydrated) return;
  if (runtimeHydratePromise) return runtimeHydratePromise;

  runtimeHydratePromise = (async () => {
    const stored = await loadStored();
    const fresh = stored.filter((e) => Date.now() - e.notifiedAt <= TTL_MS);
    runtimeNotified.clear();
    fresh.forEach((e) => runtimeNotified.set(e.id, e));
    runtimeHydrated = true;
  })().finally(() => {
    runtimeHydratePromise = null;
  });

  return runtimeHydratePromise;
};

/**
 * Hook to monitor budget alerts and send push notifications when new alerts are detected
 * 
 * This hook tracks which budget alerts have already been notified to avoid duplicate notifications.
 * When a new budget alert appears (or an existing one changes priority), it sends a push notification.
 */
export function useBudgetAlertNotifications(
  alerts: TripAlert[],
  trips: Trip[]
): void {
  const notifiedAlertIdsRef = useRef<Set<string>>(new Set());
  const previousAlertsRef = useRef<TripAlert[]>([]);

  useEffect(() => {
    // Hydrate once per runtime and mirror to this hook instance.
    ensureRuntimeHydrated()
      .then(() => {
        notifiedAlertIdsRef.current = new Set(runtimeNotified.keys());
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Find budget alerts
    const budgetAlerts = alerts.filter(alert => alert.type === 'budget');
    
    // Ensure we've hydrated persisted state before firing notifications
    if (!runtimeHydrated) return;
    
    // Find newly triggered budget alerts (ones we haven't notified about yet)
    const newBudgetAlerts = budgetAlerts.filter(alert => {
      // Check if this alert ID was already notified
      const stored = runtimeNotified.get(alert.id);
      if (stored) {
        // Allow re-notify if priority escalated since last notify.
        const priorityRank: Record<TripAlert['priority'], number> = { low: 0, medium: 1, high: 2 };
        const prevRank = priorityRank[stored.priority] ?? 0;
        const nextRank = priorityRank[alert.priority] ?? 0;
        if (nextRank <= prevRank) return false;
      } else if (notifiedAlertIdsRef.current.has(alert.id)) {
        return false;
      }
      
      // Check if this is a new alert or if the priority changed (e.g., from medium to high)
      const previousAlert = previousAlertsRef.current.find(a => a.id === alert.id);
      if (previousAlert && previousAlert.priority === alert.priority) {
        return false; // Same alert, same priority - already notified
      }
      
      return true;
    });

    // Send notifications for new alerts
    for (const alert of newBudgetAlerts) {
      // Pre-mark as notified in runtime cache to prevent duplicates during concurrent renders.
      const now = Date.now();
      runtimeNotified.set(alert.id, { id: alert.id, priority: alert.priority, notifiedAt: now });
      notifiedAlertIdsRef.current.add(alert.id);

      // Extract threshold percent for dedupe key
      const thresholdPercent = alert.budgetMetadata?.thresholdPercent ?? 0.8;
      const dedupeKey = buildDedupeKey.budget(alert.tripId, thresholdPercent);

      // Use metadata if available (preferred), otherwise fall back to parsing
      if (alert.budgetMetadata) {
        const { spentFormatted, budgetFormatted, isOverBudget } = alert.budgetMetadata;
        
        // Use gated notification with preference/dedupe/rate-limit checks
        sendGatedNotification(
          {
            category: 'expenses',
            channel: 'push',
            tripId: alert.tripId,
            priority: alert.priority,
            dedupeKey,
            cooldownMs: DEFAULT_COOLDOWNS.budget,
          },
          () => sendBudgetAlertNotification(
            alert.tripName,
            alert.tripId,
            thresholdPercent,
            spentFormatted,
            budgetFormatted,
            isOverBudget,
            alert.priority
          )
        ).catch(error => {
          console.error('[useBudgetAlertNotifications] Error sending notification:', error);
        });
      } else {
        // Fallback: try to extract from message (less reliable)
        const trip = trips.find(t => t.id === alert.tripId);
        if (!trip) continue;

        const isOverBudget = alert.title.includes('exceeded') || alert.title.includes('Budget exceeded');
        
        // Extract threshold from alert ID (format: budget-{tripId}-{thresholdPercent})
        const thresholdMatch = alert.id.match(/budget-[\w-]+-(\d+)/);
        const extractedThreshold = thresholdMatch 
          ? parseInt(thresholdMatch[1], 10) / 100 
          : 0.8;

        // Extract amounts from message using regex
        const currencySymbol = trip.baseCurrency ? formatCurrency(0, trip.baseCurrency).replace('0', '') : '$';
        const spentMatch = alert.message.match(new RegExp(`(${currencySymbol.replace('$', '\\$')}[\\d,]+)`));
        const budgetMatch = alert.message.match(new RegExp(`budget of (${currencySymbol.replace('$', '\\$')}[\\d,]+)`)) 
          || alert.message.match(new RegExp(`(${currencySymbol.replace('$', '\\$')}[\\d,]+) budget`));

        const spentAmount = spentMatch ? spentMatch[1] : 'N/A';
        const budgetAmount = budgetMatch ? budgetMatch[1] : 'N/A';

        // Use gated notification with preference/dedupe/rate-limit checks
        sendGatedNotification(
          {
            category: 'expenses',
            channel: 'push',
            tripId: alert.tripId,
            priority: alert.priority,
            dedupeKey: buildDedupeKey.budget(alert.tripId, extractedThreshold),
            cooldownMs: DEFAULT_COOLDOWNS.budget,
          },
          () => sendBudgetAlertNotification(
            alert.tripName,
            alert.tripId,
            extractedThreshold,
            spentAmount,
            budgetAmount,
            isOverBudget,
            alert.priority
          )
        ).catch(error => {
          console.error('[useBudgetAlertNotifications] Error sending notification:', error);
        });
      }

      // Persist best-effort (keep the most recent entry per id).
      const nextStored = Array.from(runtimeNotified.values())
        .filter((e) => Date.now() - e.notifiedAt <= TTL_MS)
        .sort((a, b) => b.notifiedAt - a.notifiedAt);
      persistStored(nextStored).catch(() => {});
    }

    // Cleanup: only prune by TTL.
    //
    // IMPORTANT: do NOT prune by "not currently in alerts".
    // On app startup, alerts can temporarily be empty before Firestore subscriptions
    // and derived alerts settle. If we prune during that window, we forget what we
    // already notified and will re-send on the next render — which looks like
    // "duplicate push notifications after reload".
    let didPrune = false;
    for (const [id, entry] of runtimeNotified.entries()) {
      if (Date.now() - entry.notifiedAt > TTL_MS) {
        runtimeNotified.delete(id);
        didPrune = true;
      }
    }
    if (didPrune) {
      persistStored(Array.from(runtimeNotified.values())).catch(() => {});
    }

    // Update previous alerts reference
    previousAlertsRef.current = [...budgetAlerts];
  }, [alerts, trips]);
}

